<template>
  <v-form :disabled="loadingSubmit" validate-on="invalid-input lazy" @submit.prevent="handleSubmit">
    <v-autocomplete
      v-model="selectedCountry"
      autocomplete="bank-country"
      :items="availableCountries"
      item-title="name"
      item-value="code"
      :label="t('link.selectBank.countryLabel')"
      :placeholder="t('link.selectBank.searchCountryPlaceholder')"
      :prepend-inner-icon="!selectedCountry ? 'mdi-magnify' : undefined"
      :disabled="loadingSubmit || !filteredBanks.length"
      :loading="loadingBanks"
      single-line
      clearable
      class="mb-2"
      variant="outlined"
      data-testid="select-bank-country-combobox"
    >
      <template #item="{ props: _props, item }">
        <v-list-item v-bind="_props" :title="item.name">
          <template #prepend>
            <span class="mr-2" :class="item.flag"></span>
          </template>
        </v-list-item>
      </template>
    </v-autocomplete>

    <v-autocomplete
      v-model="selectedBank"
      :items="filteredBanks"
      item-title="name"
      item-value="id"
      :label="t('link.selectBank.bankLabel')"
      :placeholder="t('link.selectBank.searchBankPlaceholder')"
      :prepend-inner-icon="!selectedBank ? 'mdi-magnify' : undefined"
      :disabled="loadingSubmit || !filteredBanks.length"
      :loading="loadingBanks"
      single-line
      clearable
      class="mb-2"
      variant="outlined"
      data-testid="select-bank-bank-combobox"
    >
      <template #item="{ props: _props, item }">
        <v-list-item v-bind="_props" :prepend-avatar="item.logo" :title="item.name"></v-list-item>
      </template>
    </v-autocomplete>

    <v-alert v-if="errorToDisplay" density="compact" class="mb-3" color="error" type="error">{{
      errorToDisplay
    }}</v-alert>

    <v-btn
      :disabled="!selectedBank || loadingSubmit"
      :loading="loadingSubmit"
      block
      color="primary"
      size="large"
      type="submit"
      variant="flat"
      data-testid="select-bank-continue-button"
    >
      {{ t("link.selectBank.continue") }}
    </v-btn>
  </v-form>
</template>

<script setup lang="ts">
import { useI18n } from "@/composables/useI18n";
import { call } from "@/firebase";
import type { Institution } from "@aircap/common/gcl";
import { captureException } from "@sentry/vue";
import "flag-icons/css/flag-icons.min.css";
import { computed, ref, watch } from "vue";

const props = defineProps<{
  accountId: string;
  bankSelected: (bankId: string) => Promise<void>;
}>();

const { t } = useI18n();

const banks = ref<Institution[]>([]);
const loadingBanks = ref(false);
const loadingSubmit = ref(false);
const errorToDisplay = ref<string | null>(null);
const selectedBank = ref<string | null>(null);
const selectedCountry = ref<string | null>(null);

const getCountryName = (code: string): string => {
  try {
    const languageNames = new Intl.DisplayNames(["en"], { type: "region" });
    return languageNames.of(code) || code;
  } catch (cause) {
    const error = new Error(`Could not get display name for region code: ${code}`, { cause });
    console.error(error);
    captureException(error);
    return code;
  }
};

const availableCountries = computed(() => {
  const countryCodes = new Set<string>();
  banks.value.forEach((bank) => {
    bank.countries.forEach((code) => countryCodes.add(code));
  });

  return Array.from(countryCodes)
    .map((code) => ({
      code: code,
      name: getCountryName(code),
      flag: `fi fi-${code.toLowerCase()}`,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
});

const filteredBanks = computed(() => {
  const selectedCountryValue = selectedCountry.value;
  if (!selectedCountryValue) {
    return banks.value;
  }
  return banks.value.filter((bank) => bank.countries.includes(selectedCountryValue));
});

watch(selectedCountry, () => {
  selectedBank.value = null;
});

const handleSubmit = async () => {
  if (loadingSubmit.value) return;
  loadingSubmit.value = true;
  errorToDisplay.value = null;
  try {
    console.log("Selecting bank...");
    if (!selectedBank.value) throw new Error("No bank selected");
    await props.bankSelected(selectedBank.value);
  } catch (cause) {
    const error = new Error("Error executing bankSelected in SelectBank component", { cause });
    console.error(error);
    captureException(error);
    errorToDisplay.value = t("link.errorOccurred");
    loadingSubmit.value = false;
  }
};

const fetchBanks = async () => {
  loadingBanks.value = true;
  errorToDisplay.value = null;
  try {
    console.log("Fetching banks...");
    const response = await call("listGclBanks")({ accountId: props.accountId });
    banks.value = response;
  } catch (cause) {
    const error = new Error("Error fetching banks in SelectBank component", { cause });
    console.error(error);
    captureException(error);
    errorToDisplay.value = t("link.errorOccurred");
  } finally {
    loadingBanks.value = false;
  }
};

void fetchBanks();
</script>
